import type { SocialMetadata } from '@/types/models'

export const SITE_METADATA = {
  siteName: 'CENDOSCAR',
  publicBaseUrl: import.meta.env.VITE_PUBLIC_BASE_URL || 'https://cendoscar.com',
  defaultTitle: 'CENDOSCAR | Centro diagnostico',
  defaultDescription: 'Centro diagnostico y de especialidades medicas en Santo Domingo Este.',
  defaultSocialImage: '/social/cendoscar-default-share-1200x630.jpg',
  locale: 'es_DO',
}

export interface ResolvedPageMetadata extends Required<Pick<SocialMetadata, 'title' | 'description' | 'imageUrl' | 'imageAlt' | 'canonicalUrl' | 'type' | 'noIndex'>> {
  publishedTime?: string
  modifiedTime?: string
}

export function stripHtml(value = '') {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

export function buildCanonicalUrl(path = '/', customUrl?: string, baseUrl = SITE_METADATA.publicBaseUrl) {
  const candidate = customUrl?.trim()
  try {
    if (candidate) {
      const parsed = new URL(candidate)
      if (parsed.protocol === 'https:' && !parsed.pathname.startsWith('/admin')) return normalizeUrl(parsed)
    }
  } catch { /* Invalid custom URLs use the calculated fallback. */ }
  const cleanPath = path.split('#')[0].split('?')[0] || '/'
  const safePath = cleanPath.startsWith('/admin') ? '/' : cleanPath
  return normalizeUrl(new URL(safePath, `${baseUrl.replace(/\/+$/, '')}/`))
}

function normalizeUrl(url: URL) {
  url.search = ''
  url.hash = ''
  url.pathname = `/${url.pathname.split('/').filter(Boolean).join('/')}`
  if (url.pathname !== '/') url.pathname = url.pathname.replace(/\/+$/, '')
  return url.toString()
}

export function validPublicImage(value?: string, baseUrl = SITE_METADATA.publicBaseUrl) {
  if (!value || /^(blob:|data:|file:)/i.test(value)) return ''
  try {
    const url = new URL(value, `${baseUrl.replace(/\/+$/, '')}/`)
    if (url.protocol !== 'https:' && url.hostname !== 'localhost') return ''
    return url.toString()
  } catch { return '' }
}

export function resolvePageMetadata(input: {
  path: string
  socialMetadata?: SocialMetadata
  title?: string
  description?: string
  body?: string
  imageUrl?: string
  imageAlt?: string
  type?: SocialMetadata['type']
  noIndex?: boolean
}): ResolvedPageMetadata {
  const social = input.socialMetadata || {}
  const title = social.title?.trim() || input.title?.trim() || SITE_METADATA.defaultTitle
  const description = (social.description?.trim() || input.description?.trim() || stripHtml(input.body) || SITE_METADATA.defaultDescription).slice(0, 220)
  const imageUrl = validPublicImage(social.imageUrl) || validPublicImage(input.imageUrl) || validPublicImage(SITE_METADATA.defaultSocialImage)
  return {
    title,
    description,
    imageUrl,
    imageAlt: social.imageAlt?.trim() || input.imageAlt?.trim() || title,
    canonicalUrl: buildCanonicalUrl(input.path, social.canonicalUrl),
    type: social.type || input.type || 'website',
    noIndex: social.noIndex ?? input.noIndex ?? false,
  }
}

export function buildWhatsAppContextMessage(input: { type: 'article' | 'specialty' | 'service' | 'doctor'; title: string; url: string }) {
  const messages = {
    specialty: `Hola, deseo recibir informacion sobre la especialidad de ${input.title} en CENDOSCAR.`,
    doctor: `Hola, deseo consultar disponibilidad para una cita con ${input.title}.`,
    service: `Hola, deseo informacion sobre el servicio ${input.title}.`,
    article: `Hola, deseo informacion relacionada con este articulo de CENDOSCAR:\n\n${input.title}`,
  }
  return `${messages[input.type]}\n\nPagina:\n${input.url}`
}
